import { Injectable, OnDestroy } from '@angular/core';
import DOMPurify from 'dompurify';
import { CommonConstants } from '../shared';
import { CitationModel } from '../modules/wk-document-decorator';

const ATTR_NOT_TO_REMOVE = [
  'about',
  'resource',
  'property',
  'typeof',
  'rel',
  'data-suggestion-start-before',
  'data-suggestion-end-after',
  'data-suggestion-start-after',
  'data-suggestion-end-before',
];
export const FORBID_ATTR_TO_REMOVE = [
  'listitemid',
  'data-noone',
  'alignment',
  'data-subtemplates-inserted',
  'data-section-withsubtemplate',
  'data-fragment',
  'data-currentfocused-element',
  'listtype',
  'liststyle',
  'redirectLink',
  'data-parentcitation',
  'data-template',
  'data-subtemplates',
  CommonConstants.DATA_SOURCE_ID,
  CommonConstants.DATA_CKE_TOOLTIP_TEXT,
  CommonConstants.DATA_CKE_TOOLTIP_POSITION,
  CommonConstants.DATA_CKE_TOOLTIP_CLASS,
  CommonConstants.dataCitationCountPlaceholder,
  CommonConstants.numberedCitation,
];

@Injectable({
  providedIn: 'root',
})
export class SanitizeService implements OnDestroy {
  private readonly parser: DOMParser = new DOMParser();

  constructor() {
    DOMPurify.addHook('uponSanitizeAttribute', function (_, hookEvent) {
      if (ATTR_NOT_TO_REMOVE.includes(hookEvent.attrName)) {
        hookEvent.forceKeepAttr = true;
      }
    });

    DOMPurify.addHook('uponSanitizeElement', (node, data) => {
      if (node.nodeName?.match(/^\w+-\w+$/)) {
        data.allowedTags[data.tagName] = true;
      }
    });
  }

  /**
   * This method is used to sanitize the html string and remove unwanted attributes from the html string.
   * Note: This method only use in the citations service to sanitize the editor data before sending it to the server.
   *
   * @param html string to be sanitized
   * @returns html string after sanitization
   */
  public sanitizeHtml = (html: string) => {
    if (html.trim() === '') {
      return html;
    }
    const doc = this.parser.parseFromString(html, 'text/html');
    const content = doc.body;
    let header;
    if (content.children[0]?.tagName === 'HEADER') {
      header = content.querySelector('header');
      header.remove();
    }
    const dirty = content.outerHTML;

    DOMPurify.addHook('afterSanitizeAttributes', function (node) {
      if (node.tagName?.toLowerCase() === 'img') {
        if (!node.hasAttribute('loading')) {
          node.setAttribute('loading', 'lazy');
        }
        if (!node.hasAttribute('decoding')) {
          node.setAttribute('decoding', 'async');
        }
        if (!node.hasAttribute('alt')) {
          node.setAttribute('alt', '');
        }
      }
    });

    let result = '';
    try {
      result = this.sanitize(dirty);
    } catch (error) {
      console.error('Error sanitizing HTML:', error);
    }
    if (header) {
      const body = this.parser.parseFromString(result, 'text/html').body;
      body.prepend(header);
      doc.body.innerHTML = body.innerHTML;
    } else {
      doc.body.innerHTML = result;
    }
    return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
  };

  private sanitize(dirtyHtml: string) {
    return DOMPurify.sanitize(dirtyHtml, {
      SAFE_FOR_XML: true,
      ADD_ATTR: ATTR_NOT_TO_REMOVE,
      FORBID_ATTR: FORBID_ATTR_TO_REMOVE,
      CUSTOM_ELEMENT_HANDLING: {
        tagNameCheck: (tagName) =>
          tagName === 'suggestion-end' || tagName === 'suggestion-start',
      },
    });
  }

  public sanitizeCitation(citations: CitationModel): CitationModel {
    if (citations?.references) {
      citations.references = citations.references.map((reference) => {
        reference.citationString = this.sanitize(reference.citationString);
        return reference;
      });
    }
    if (citations?.unnumbered_references) {
      citations.unnumbered_references = citations.unnumbered_references.map(
        (reference) => {
          reference.citationString = this.sanitize(reference.citationString);
          return reference;
        }
      );
    }
    return citations;
  }

  ngOnDestroy(): void {
    DOMPurify.removeAllHooks();
  }
}
